// 1 RUB = 250 credits for display (as mentioned in backend comments)
export const convertRublesToCredits = (rubles: number): number => {
  return Math.floor(rubles * 250);
};

export const formatCreditsNumber = (amount: number): string => {
  return new Intl.NumberFormat('ru-RU').format(amount);
};

/**
 * Dollar amount of an item's reference Steam price, for `ItemPrice`.
 *
 * Thousands are grouped by `formatCreditsNumber` — the very formatter the credits number
 * uses — because both numbers stand in one price row and a row where one number groups its
 * digits and the other doesn't reads as two different units. The decimal mark stays a dot:
 * the dollar sign implies it, and the group separator is a space, so the two never collide.
 *
 * Under a cent the amount keeps four decimals, because cheap skins exist and `0.00` would
 * hide the price entirely. Exact zero has no cents at all, so lists of free items don't
 * fill up with `$0.00`.
 *
 * `whole: true` drops the cents for the tight contest cards, next to `Credits compact`.
 */
export const formatUsdPrice = (amount: number, opts?: { whole?: boolean }): string => {
  if (!(amount > 0)) return '0';

  const digits = opts?.whole ? 0 : amount < 0.01 ? 4 : 2;
  const [int, frac] = amount.toFixed(digits).split('.');
  const grouped = formatCreditsNumber(Number(int));

  return frac ? `${grouped}.${frac}` : grouped;
};

export const formatCreditsShort = (amount: number): string => {
  if (amount >= 1000000) {
    const millions = amount / 1000000;
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
  }
  if (amount >= 1000) {
    const thousands = amount / 1000;
    return thousands % 1 === 0 ? `${thousands}k` : `${thousands.toFixed(1)}k`;
  }
  return amount.toString();
};

// Extract skin name from full item name
export const extractSkinName = (fullName: string): string => {
  // Check if it's a Sealed Graffiti
  if (fullName.startsWith('Sealed Graffiti')) {
    return 'Sealed Graffiti';
  }

  // Check if it's a Sticker
  if (fullName.startsWith('Sticker')) {
    return 'Sticker';
  }

  // Remove ★, Souvenir and StatTrak™ prefixes if exist
  const nameWithoutPrefixes = fullName.replace(/^★\s*/, '').replace(/^Souvenir\s+/, '').replace(/^StatTrak™\s+/, '');

  // Split by | to get the skin part
  const parts = nameWithoutPrefixes.split('|');
  if (parts.length < 2) return fullName;

  // Get skin name and remove wear condition in parentheses
  const skinPart = parts[1].trim();
  const skinName = skinPart.replace(/\s*\([^)]*\)\s*$/, '').trim();

  return skinName;
};

// Abbreviate wear condition to two letters
export const abbreviateWearCondition = (condition: string): string => {
  const abbreviations: Record<string, string> = {
    'Factory New': 'FN',
    'Minimal Wear': 'MW',
    'Field-Tested': 'FT',
    'Well-Worn': 'WW',
    'Battle-Scarred': 'BS',
  };

  return abbreviations[condition] || condition;
};

export interface ParsedItemDetails {
  weaponName: string;
  skinName: string;
  condition: string;
  isStar: boolean;
  isStatTrak: boolean;
  isSouvenir: boolean;
}

export const parseItemDetails = (fullName: string): ParsedItemDetails => {
  // Handle special cases: Sealed Graffiti, Stickers
  const specialPrefix = fullName.startsWith('Sealed Graffiti') ? 'Graffiti'
    : fullName.startsWith('Sticker') ? 'Sticker'
    : null;

  if (specialPrefix) {
    const parts = fullName.split('|');
    let pattern = specialPrefix;
    let color = '';
    if (parts.length >= 2) {
      const rightPart = parts[1].trim();
      const match = rightPart.match(/^(.+?)\s*\((.+?)\)$/);
      if (match) {
        pattern = match[1].trim();
        color = match[2].trim();
      } else {
        pattern = rightPart;
      }
    }
    return {
      weaponName: specialPrefix,
      skinName: pattern,
      condition: color,
      isStar: false, isStatTrak: false, isSouvenir: false,
    };
  }

  // Handle case/capsule names: "Spectrum 2 Case" → weapon: "Case", skin: "Spectrum 2"
  const caseMatch = fullName.match(/^(.+?)\s+(Case|Capsule|Package|Collection)$/i);
  if (caseMatch) {
    return {
      weaponName: caseMatch[2],
      skinName: caseMatch[1],
      condition: '',
      isStar: false, isStatTrak: false, isSouvenir: false,
    };
  }

  // Check for ★, StatTrak and Souvenir
  const isStar = fullName.startsWith('★');
  const isStatTrak = fullName.includes('StatTrak™');
  const isSouvenir = fullName.startsWith('Souvenir');

  // Remove prefixes (★ for rare items like knives/gloves, StatTrak™, Souvenir)
  // Order matters: ★ can precede StatTrak™ (e.g., "★ StatTrak™ Karambit | Doppler")
  const nameWithoutPrefixes = fullName
    .replace(/^★\s*/, '')
    .replace(/^StatTrak™\s+/, '')
    .replace(/^Souvenir\s+/, '')
    .trim();

  // Split by | to get weapon and skin parts
  const parts = nameWithoutPrefixes.split(' | ');
  if (parts.length < 2) {
    return { weaponName: nameWithoutPrefixes, skinName: '', condition: '', isStar, isStatTrak, isSouvenir };
  }

  const weaponName = parts[0].trim();
  const skinWithWear = parts[1].trim();

  // Extract wear condition from parentheses
  const wearMatch = skinWithWear.match(/\((.*?)\)$/);
  const condition = wearMatch ? wearMatch[1] : '';
  const skinName = condition ? skinWithWear.replace(/\s*\([^)]*\)\s*$/, '').trim() : skinWithWear;

  return { weaponName, skinName, condition, isStar, isStatTrak, isSouvenir };
};

// Format time remaining for auctions and other countdowns
export const formatTimeRemaining = (timeRemaining: number, endedText: string = 'Ended'): string => {
  if (timeRemaining <= 0) return endedText;

  const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

